<script setup lang="ts">
import { computed } from 'vue';
import { ProgressColor } from '../progress.constants';
import { ProgressCircleSize } from './progress-circle.constants';

type Props = {
  /** Completion percentage. Values outside 0–100 are clamped. Ignored when `indeterminate`. */
  progress?: number;
  /** Accent color of the ring. */
  color?: ProgressColor;
  /** Rendered size: `sm` 24px, `md` 32px, `lg` 40px. */
  size?: ProgressCircleSize;
  /** Whether to render the percentage label inside the ring. */
  showLabel?: boolean;
  /** Spins a quarter arc on a tinted track instead of showing a value — for loading states. */
  indeterminate?: boolean;
  /** Accessible name, rendered as `aria-label`. */
  label?: string;
};

const props = withDefaults(defineProps<Props>(), {
  progress: 0,
  color: ProgressColor.PRIMARY,
  size: ProgressCircleSize.MD,
  showLabel: true,
  indeterminate: false,
  label: undefined,
});

const colorClass: Record<ProgressColor, string> = {
  [ProgressColor.PRIMARY]: 'text-primary',
  [ProgressColor.SECONDARY]: 'text-secondary',
  [ProgressColor.TERTIARY]: 'text-tertiary',
  [ProgressColor.NEUTRAL]: 'text-neutral',
  [ProgressColor.SUCCESS]: 'text-success',
  [ProgressColor.ERROR]: 'text-error',
};

// Ring geometry replicating the Figma Small/Medium/Large variants: the stroke
// is centered on the frame edge, so the ring intentionally overflows the box
// by half the stroke.
const dimensions: Record<
  ProgressCircleSize,
  { px: number; stroke: number; radius: number }
> = {
  [ProgressCircleSize.SM]: { px: 24, stroke: 3, radius: 12 },
  [ProgressCircleSize.MD]: { px: 32, stroke: 4, radius: 16 },
  [ProgressCircleSize.LG]: { px: 40, stroke: 4, radius: 20 },
};

// Figma's Progress/Small|Medium|Large text styles, deliberately kept off the
// shared type ramp.
const labelClass: Record<ProgressCircleSize, string> = {
  [ProgressCircleSize.SM]: 'text-[0.5rem]/[0.75rem]',
  [ProgressCircleSize.MD]: 'text-[0.625rem]/[0.875rem]',
  [ProgressCircleSize.LG]: 'text-[0.75rem]/[1rem]',
};

const INDETERMINATE_ARC_FRACTION = 0.25;
const TRACK_OPACITY = 0.4;

const clampedProgress = computed(() =>
  Math.round(Math.min(Math.max(props.progress, 0), 100)),
);
const px = computed(() => dimensions[props.size].px);
const strokeWidth = computed(() => dimensions[props.size].stroke);
const radius = computed(() => dimensions[props.size].radius);
const circumference = computed(() => 2 * Math.PI * radius.value);
const arcFraction = computed(() =>
  props.indeterminate
    ? INDETERMINATE_ARC_FRACTION
    : clampedProgress.value / 100,
);
const dashArray = computed(
  () => `${arcFraction.value * circumference.value} ${circumference.value}`,
);
const isArcVisible = computed(() => arcFraction.value > 0);
const isLabelVisible = computed(() => !props.indeterminate && props.showLabel);
const valueAttributes = computed(() =>
  props.indeterminate
    ? {}
    : {
        'aria-valuemin': 0,
        'aria-valuemax': 100,
        'aria-valuenow': clampedProgress.value,
      },
);
</script>

<template>
  <div
    class="relative inline-flex items-center justify-center"
    role="progressbar"
    :aria-label="label"
    v-bind="valueAttributes"
  >
    <!-- Rotated so the arc starts at 12 o'clock and grows clockwise. -->
    <svg
      :width="px"
      :height="px"
      :viewBox="`0 0 ${px} ${px}`"
      fill="none"
      class="-rotate-90 overflow-visible"
      :class="[
        colorClass[color],
        { 'motion-safe:animate-spin': indeterminate },
      ]"
    >
      <circle
        v-if="indeterminate"
        :cx="px / 2"
        :cy="px / 2"
        :r="radius"
        stroke="currentColor"
        :stroke-width="strokeWidth"
        :stroke-opacity="TRACK_OPACITY"
      />
      <!-- Hidden at 0% — a zero-length dash would still paint a round cap. -->
      <circle
        v-if="isArcVisible"
        :cx="px / 2"
        :cy="px / 2"
        :r="radius"
        stroke="currentColor"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
        :stroke-dasharray="dashArray"
        class="transition-[stroke-dasharray] duration-300 ease-in-out"
      />
    </svg>
    <!-- Always on-surface-primary, regardless of the ring color variant. -->
    <span
      v-if="isLabelVisible"
      class="absolute font-semibold text-on-surface-primary"
      :class="labelClass[size]"
    >
      {{ clampedProgress }}%
    </span>
  </div>
</template>
